// "What's new": a short announcement of the AI features that are live in this
// environment, shown once per account per announcement version and kept in
// the app menu to reopen.

// Bump when the announcement changes (a new card, new wording that should be
// seen again): everyone sees it once more and the menu dot comes back.
export const WHATS_NEW_VERSION = "2026-10-shortcuts";

// At most this many cards; the most recent features come first.
export const WHATS_NEW_MAX_CARDS = 6;

export type WhatsNewFeatureId =
  | "workspace"
  | "upload"
  | "export"
  | "offline"
  | "templates"
  | "letters"
  | "shortcuts";

// Where "Try it" takes the provider.
// - visit: the latest visit, then opens or points to a part of it.
// - newVisit: the Visits page with the new visit panel open.
// - route: another page of the app.
export type VisitFocus = "assistant" | "upload" | "export" | "create";
export type WhatsNewAction =
  | { kind: "visit"; focus: VisitFocus }
  | { kind: "newVisit" }
  | { kind: "route"; to: "/template/new" | "/templates"; hash?: string };

export interface WhatsNewFeature {
  id: WhatsNewFeatureId;
  action: WhatsNewAction;
  // Shows "Learn more" with the feature's extra line.
  learnMore: boolean;
}

// In order of priority: the features of the last weeks first, then the
// earlier AI features that people may not have found yet.
const FEATURES: readonly WhatsNewFeature[] = [
  // Workspace: the note with the assistant (voice, fact check) beside it.
  {
    id: "workspace",
    action: { kind: "visit", focus: "assistant" },
    learnMore: true,
  },
  {
    id: "upload",
    action: { kind: "visit", focus: "upload" },
    learnMore: true,
  },
  {
    id: "export",
    action: { kind: "visit", focus: "export" },
    learnMore: false,
  },
  {
    id: "offline",
    action: { kind: "newVisit" },
    learnMore: true,
  },
  // Spoken shortcuts work in the assistant everywhere, so they lead the
  // earlier features.
  {
    id: "shortcuts",
    action: { kind: "route", to: "/templates", hash: "shortcuts" },
    learnMore: true,
  },
  {
    id: "templates",
    action: { kind: "route", to: "/template/new" },
    learnMore: false,
  },
  {
    id: "letters",
    action: { kind: "visit", focus: "create" },
    learnMore: false,
  },
];

export const whatsNewFeatures = (): WhatsNewFeature[] =>
  FEATURES.slice(0, WHATS_NEW_MAX_CARDS);

// Seen = shown automatically and closed (any way), or opened from the menu.
// Remembered in this browser per account and version, read and written with
// the same try/catch convenience pattern as the onboarding gates. Without
// storage (private mode) it is remembered for this page load only.
type Account = number | string | null | undefined;
const SEEN_PREFIX = "whatsNew:seen";
const seenThisLoad = new Set<string>();

export const whatsNewSeenKey = (
  account: Account,
  version: string = WHATS_NEW_VERSION,
): string =>
  account === null || account === undefined || account === ""
    ? `${SEEN_PREFIX}:${version}`
    : `${SEEN_PREFIX}:${version}:${account}`;

export const hasSeenWhatsNew = (
  account: Account,
  version: string = WHATS_NEW_VERSION,
): boolean => {
  const key = whatsNewSeenKey(account, version);
  if (seenThisLoad.has(key)) return true;
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

export const markWhatsNewSeen = (
  account: Account,
  version: string = WHATS_NEW_VERSION,
): void => {
  const key = whatsNewSeenKey(account, version);
  seenThisLoad.add(key);
  try {
    window.localStorage.setItem(key, "true");
  } catch {
    // A convenience only.
  }
};

// Tests only: forget what this page load remembered.
export const resetWhatsNewMemory = (): void => {
  seenThisLoad.clear();
};

// Shown by itself once per account and version, never over the first-time
// welcome: while the welcome is open, or still due for this account, the
// announcement waits and comes after it. A brand-new account does not get it
// by itself before its first visit is done (see isFirstRunPending); it stays
// in the menu.
export const shouldAutoOpenWhatsNew = ({
  enabled,
  account,
  seen,
  welcomeOpen,
  welcomeDue,
  firstRunPending = false,
  hasFeatures,
}: {
  enabled: boolean;
  account: string | undefined;
  seen: boolean;
  welcomeOpen: boolean;
  welcomeDue: boolean;
  firstRunPending?: boolean;
  hasFeatures: boolean;
}): boolean =>
  enabled &&
  account !== undefined &&
  hasFeatures &&
  !seen &&
  !welcomeOpen &&
  !welcomeDue &&
  !firstRunPending;
